import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { ArrowLeft, CheckCircle2, Clock3, Flag, Layers3 } from 'lucide-react';
import api from '../services/api';

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
}

export default function Missions() {
  const navigate = useNavigate();
  const [selectedCompletedMissionId, setSelectedCompletedMissionId] = useState(null);

  const { data, isLoading, error } = useQuery(
    'missionsOverview',
    async () => {
      const response = await api.get('/missions/overview');
      return response.data;
    },
    { refetchOnWindowFocus: false }
  );

  const completedMissions = Array.isArray(data?.completedMissions) ? data.completedMissions : [];
  const activeMission = data?.activeMission || null;
  const selectedCompletedMission = useMemo(
    () => completedMissions.find((entry) => Number(entry.id_mission) === Number(selectedCompletedMissionId)) || null,
    [completedMissions, selectedCompletedMissionId]
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#04060d] text-cyan-200 flex items-center justify-center">
        Cargando misiones...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#04060d] text-rose-200 flex items-center justify-center px-6 text-center">
        {error.response?.data?.error || 'No se pudo cargar el panel de misiones'}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#04060d] text-slate-100">
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="mb-3 inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800/60"
            >
              <ArrowLeft size={16} /> Volver al Dashboard
            </button>
            <h1 className="text-3xl font-bold text-cyan-300">Misiones</h1>
            <p className="mt-2 text-sm text-slate-400">
              Nivel del piloto: <span className="text-cyan-200 font-semibold">{data?.playerLevel || 1}</span>
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-cyan-500/20 bg-[#091427] px-4 py-3">
              <div className="text-xs uppercase tracking-[0.25em] text-cyan-300/70">Activa</div>
              <div className="mt-1 text-2xl font-bold text-white">{activeMission ? 1 : 0}</div>
            </div>
            <div className="rounded-2xl border border-emerald-500/20 bg-[#081420] px-4 py-3">
              <div className="text-xs uppercase tracking-[0.25em] text-emerald-300/70">Finalizadas</div>
              <div className="mt-1 text-2xl font-bold text-white">{completedMissions.length}</div>
            </div>
          </div>
        </div>

        <section className="rounded-3xl border border-cyan-500/20 bg-[#08111f] p-6 shadow-[0_0_30px_rgba(0,255,255,0.06)]">
          <div className="flex items-center gap-3">
            <Flag className="text-cyan-300" size={22} />
            <h2 className="text-xl font-bold text-white">Mision activa</h2>
          </div>

          {!activeMission ? (
            <p className="mt-4 text-sm text-slate-400">
              No tienes ninguna mision activa ahora mismo.
            </p>
          ) : (
            <div className="mt-5 space-y-4">
              <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/5 p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-cyan-300/70">En curso</p>
                    <h3 className="mt-1 text-2xl font-bold text-white">{activeMission.mission.name}</h3>
                    <p className="mt-2 text-sm text-slate-300">{activeMission.mission.description || 'Sin descripcion'}</p>
                  </div>
                  <div className="text-sm text-slate-300">
                    <div>Iniciada: <span className="text-white">{formatDate(activeMission.started_at)}</span></div>
                    <div>Progreso visible: <span className="text-cyan-200 font-semibold">{activeMission.completedEventsCount || 0}/{activeMission.totalEventsCount || 0}</span></div>
                  </div>
                </div>
              </div>

              {activeMission.events.length === 0 ? (
                <div className="rounded-2xl border border-slate-700 bg-slate-900/50 p-4 text-sm text-slate-400">
                  Aun no has completado ningun evento de esta mision. Los siguientes objetivos permanecen ocultos hasta descubrirlos en el juego.
                </div>
              ) : (
                <div className="space-y-3">
                  {activeMission.events.map((eventRow) => (
                    <article
                      key={eventRow.id}
                      className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-emerald-200"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="text-xs uppercase tracking-[0.25em] opacity-80">Fase {eventRow.phaseNumber}</p>
                          <h4 className="mt-1 font-bold text-white/95">Completada</h4>
                        </div>
                        <div className="text-xs opacity-80">Evento registrado</div>
                      </div>
                      <p className="mt-3 text-sm">{eventRow.event_text || 'Sin texto narrativo'}</p>
                      {eventRow.summary?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {eventRow.summary.map((entry) => (
                            <span key={`${eventRow.id}-${entry}`} className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs">
                              {entry}
                            </span>
                          ))}
                        </div>
                      )}
                    </article>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-6">
          <div className="rounded-3xl border border-emerald-500/20 bg-[#08111f] p-6">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="text-emerald-300" size={22} />
              <h2 className="text-xl font-bold text-white">Misiones finalizadas</h2>
            </div>

            <div className="mt-5 space-y-3">
              {completedMissions.length === 0 && (
                <p className="text-sm text-slate-400">Todavia no has finalizado ninguna mision.</p>
              )}

              {completedMissions.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  onClick={() => setSelectedCompletedMissionId(Number(entry.id_mission))}
                  className={`w-full rounded-2xl border p-4 text-left transition-all ${
                    Number(selectedCompletedMissionId) === Number(entry.id_mission)
                      ? 'border-emerald-400/40 bg-emerald-500/10'
                      : 'border-slate-800 bg-slate-900/40 hover:border-slate-600'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-bold text-white">{entry.mission.name}</h3>
                      <p className="mt-1 text-sm text-slate-400">{entry.mission.description || 'Sin descripcion'}</p>
                    </div>
                    <Clock3 size={18} className="text-emerald-300" />
                  </div>
                  <p className="mt-3 text-xs text-slate-500">Finalizada: {formatDate(entry.completed_at)}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-slate-700 bg-[#08111f] p-6">
            <div className="flex items-center gap-3">
              <Layers3 className="text-cyan-300" size={22} />
              <h2 className="text-xl font-bold text-white">Eventos completados</h2>
            </div>

            {!selectedCompletedMission ? (
              <p className="mt-5 text-sm text-slate-400">
                Selecciona una mision finalizada para ver solo los eventos que ya quedaron registrados.
              </p>
            ) : (
              <div className="mt-5 space-y-4">
                <div className="rounded-2xl border border-slate-700 bg-slate-900/50 p-4">
                  <h3 className="text-lg font-bold text-white">{selectedCompletedMission.mission.name}</h3>
                  <p className="mt-2 text-sm text-slate-300">{selectedCompletedMission.mission.description || 'Sin descripcion'}</p>
                  <p className="mt-3 text-xs text-slate-500">Completada: {formatDate(selectedCompletedMission.completed_at)}</p>
                </div>

                {selectedCompletedMission.events.map((eventRow) => (
                  <article key={eventRow.id} className="rounded-2xl border border-slate-700 bg-slate-900/50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs uppercase tracking-[0.25em] text-slate-500">Fase {eventRow.phaseNumber}</p>
                        <h4 className="mt-1 font-bold text-emerald-200">Completada</h4>
                      </div>
                      <div className="text-xs text-slate-500">Evento registrado</div>
                    </div>
                    <p className="mt-3 text-sm text-slate-200">{eventRow.event_text || 'Sin texto narrativo'}</p>
                    {eventRow.summary?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {eventRow.summary.map((entry) => (
                          <span key={`${eventRow.id}-${entry}`} className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs text-emerald-200">
                            {entry}
                          </span>
                        ))}
                      </div>
                    )}
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
